import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import {
  RefreshCw,
  Filter,
  X,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  MoreHorizontal,
  ExternalLink,
  Clock,
  GitPullRequest,
  MessageSquare,
  FileText,
  Link2
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const SEVERITY_CONFIG = {
  critical: {
    color: 'bg-red-500',
    textColor: 'text-red-500',
    bgLight: 'bg-red-500/10',
    icon: AlertTriangle,
    label: 'Critical'
  },
  high: {
    color: 'bg-orange-500',
    textColor: 'text-orange-500',
    bgLight: 'bg-orange-500/10',
    icon: AlertCircle,
    label: 'High'
  },
  medium: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-500',
    bgLight: 'bg-yellow-500/10',
    icon: AlertCircle,
    label: 'Medium'
  },
  low: {
    color: 'bg-blue-500',
    textColor: 'text-blue-500',
    bgLight: 'bg-blue-500/10',
    icon: Info,
    label: 'Low'
  },
};

const TYPE_CONFIG = {
  stalled_pr: {
    icon: GitPullRequest,
    label: 'Stalled PR',
    description: 'Pull request without recent activity'
  },
  blocked_issue: {
    icon: Link2,
    label: 'Blocked Issue',
    description: 'Issue blocked by unresolved dependencies'
  },
  high_discussion: {
    icon: MessageSquare,
    label: 'High Discussion',
    description: 'Unusual activity volume detected'
  },
  dependency_change: {
    icon: FileText,
    label: 'Dependency Change',
    description: 'Documentation change affecting code'
  },
};

export default function ProjectAlertsTab({ project }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    resolved: 'false', // Default to unresolved
  });

  // Resolve dialog
  const [resolveDialog, setResolveDialog] = useState({ open: false, alert: null });
  const [resolveReason, setResolveReason] = useState('');
  const [resolving, setResolving] = useState(false);

  const loadAlerts = useCallback(async (showRefresh = false) => {
    if (!project?.id) return;

    try {
      if (showRefresh) setRefreshing(true);

      const params = new URLSearchParams();
      params.append('project_id', project.id);
      if (filters.type) params.append('type', filters.type);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.resolved) params.append('resolved', filters.resolved);

      const response = await api.get(`/alerts?${params}`);
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [project?.id, filters]);

  useEffect(() => {
    loadAlerts();
    // Refresh every 60 seconds
    const interval = setInterval(() => loadAlerts(), 60000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      severity: '',
      resolved: 'false',
    });
  };

  const hasActiveFilters = filters.type || filters.severity || filters.resolved !== 'false';

  const handleResolve = async (reason) => {
    if (!resolveDialog.alert) return;

    setResolving(true);
    try {
      await api.post(`/alerts/${resolveDialog.alert.id}/resolve`, {
        reason: reason || 'dismissed'
      });
      toast.success('Alert resolved');
      setResolveDialog({ open: false, alert: null });
      setResolveReason('');
      loadAlerts();
    } catch (error) {
      console.error('Failed to resolve alert:', error);
      toast.error('Failed to resolve alert');
    } finally {
      setResolving(false);
    }
  };

  const handleUnresolve = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/unresolve`);
      toast.success('Alert reopened');
      loadAlerts();
    } catch (error) {
      console.error('Failed to unresolve alert:', error);
      toast.error('Failed to reopen alert');
    }
  };

  const openExternalLink = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getResourceUrl = (alert) => {
    const metadata = alert.metadata || {};
    return metadata.pr_url || metadata.issue_url || metadata.doc_url || null;
  };

  // Summary stats
  const activeAlerts = alerts.filter(a => !a.resolved);
  const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length;
  const highCount = activeAlerts.filter(a => a.severity === 'high').length;

  return (
    <div className="space-y-6">
      {/* Summary Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Project Alerts</h2>
          {activeAlerts.length > 0 && (
            <div className="flex items-center gap-2">
              {criticalCount > 0 && (
                <Badge variant="destructive">{criticalCount} Critical</Badge>
              )}
              {highCount > 0 && (
                <Badge className="bg-orange-500">{highCount} High</Badge>
              )}
              <span className="text-sm text-muted-foreground">
                {activeAlerts.length} active alert{activeAlerts.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadAlerts(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="h-4 w-4" />
            <span>Filters:</span>
          </div>

          <Select value={filters.type || 'all'} onValueChange={(v) => updateFilter('type', v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.severity || 'all'} onValueChange={(v) => updateFilter('severity', v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.resolved || 'false'} onValueChange={(v) => updateFilter('resolved', v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">Active</SelectItem>
              <SelectItem value="true">Resolved</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Alerts Table */}
      <Card>
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">Loading alerts...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-medium">No alerts found</h3>
            <p className="text-muted-foreground">
              {hasActiveFilters
                ? 'Try adjusting your filters'
                : 'Everything looks good! No active alerts for this project.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Severity</TableHead>
                <TableHead className="w-[140px]">Type</TableHead>
                <TableHead>Message</TableHead>
                <TableHead className="w-[120px]">Created</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => {
                const severityConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
                const typeConfig = TYPE_CONFIG[alert.type] || { icon: AlertCircle, label: alert.type };
                const SeverityIcon = severityConfig.icon;
                const TypeIcon = typeConfig.icon;
                const resourceUrl = getResourceUrl(alert);

                return (
                  <TableRow key={alert.id} className={alert.resolved ? 'opacity-60' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded ${severityConfig.bgLight}`}>
                          <SeverityIcon className={`h-4 w-4 ${severityConfig.textColor}`} />
                        </div>
                        <span className="text-sm font-medium">{severityConfig.label}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{typeConfig.label}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-md">
                        <p className="text-sm truncate">{alert.message}</p>
                        {resourceUrl && (
                          <button
                            onClick={() => openExternalLink(resourceUrl)}
                            className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View resource
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatTimeAgo(alert.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {alert.resolved ? (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          Resolved
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-600 border-orange-600">
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {resourceUrl && (
                            <DropdownMenuItem onClick={() => openExternalLink(resourceUrl)}>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Open Resource
                            </DropdownMenuItem>
                          )}
                          {alert.resolved ? (
                            <DropdownMenuItem onClick={() => handleUnresolve(alert.id)}>
                              <AlertCircle className="h-4 w-4 mr-2" />
                              Reopen Alert
                            </DropdownMenuItem>
                          ) : (
                            <>
                              <DropdownMenuItem onClick={() => handleResolve('fixed')}>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Mark as Fixed
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setResolveDialog({ open: true, alert })}>
                                <X className="h-4 w-4 mr-2" />
                                Dismiss...
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialog.open} onOpenChange={(open) => !open && setResolveDialog({ open: false, alert: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dismiss Alert</DialogTitle>
            <DialogDescription>
              Provide a reason for dismissing this alert. This helps track why alerts were resolved.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="e.g., False positive, Already addressed, Not applicable..."
              value={resolveReason}
              onChange={(e) => setResolveReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog({ open: false, alert: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => handleResolve(resolveReason || 'dismissed')}
              disabled={resolving}
            >
              {resolving ? 'Dismissing...' : 'Dismiss Alert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
